// src/shared/components/PerlSelect.jsx
import React from "react";
import { Select, FormControl, InputLabel, MenuItem, useTheme, alpha } from "@mui/material";

/**
 * PerlSelect - Shared dropdown component
 * Used by: F&B, Mechanical, Electronics, Packaging
 *
 * Reusable Select dropdown component with consistent styling
 * Follows the same pattern as PerlTextField
 */
export default function PerlSelect({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  options = [],
  showLabel = true,
  size = "small",
  fullWidth = true,
  ...props
}) {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#FFFFFF",
    textPrimary: "#F1F2F3",
    textSecondary: "#798286",
    surface: "#191A1B",
    border: "#2A2B2C",
  };
  const brandColor = theme.custom?.brandColor || "#ff6034";

  // Merge MenuProps with any provided in props
  const defaultMenuProps = {
    disablePortal: true,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
    PaperProps: {
      sx: {
        bgcolor: perlColors.surface,
        border: `1px solid ${alpha(perlColors.textPrimary, 0.12)}`,
        borderRadius: 1.5,
        mt: 0.5,
        maxHeight: 300,
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        "& .MuiMenuItem-root": {
          color: perlColors.textPrimary,
          "&:hover": {
            bgcolor: alpha(brandColor, 0.1),
          },
          "&.Mui-selected": {
            bgcolor: alpha(brandColor, 0.2),
            "&:hover": {
              bgcolor: alpha(brandColor, 0.3),
            },
          },
        },
      },
    },
  };

  const mergedMenuProps = {
    ...defaultMenuProps,
    ...(props.MenuProps || {}),
    PaperProps: {
      ...defaultMenuProps.PaperProps,
      ...(props.MenuProps?.PaperProps || {}),
      sx: {
        ...defaultMenuProps.PaperProps.sx,
        ...(props.MenuProps?.PaperProps?.sx || {}),
      },
    },
  };

  // Extract MenuProps from props to avoid passing it twice
  const { MenuProps: _, ...restProps } = props;

  return (
    <FormControl fullWidth={fullWidth} size={size} disabled={disabled}>
      {showLabel && label && (
        <InputLabel
          sx={{
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": {
              color: perlColors.textPrimary,
            },
          }}
        >
          {label}
        </InputLabel>
      )}
      <Select
        value={value || ""}
        onChange={onChange}
        label={showLabel && label ? label : undefined}
        placeholder={placeholder}
        displayEmpty={!!placeholder}
        MenuProps={mergedMenuProps}
        sx={{
          bgcolor: alpha(perlColors.white, 0.02),
          color: perlColors.textPrimary,
          fontSize: "0.9rem",
          borderRadius: 1.5,
          "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
          "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
          "&.Mui-focused fieldset": {
            borderColor: brandColor,
            boxShadow: `0 0 0 2px ${brandColor}33`,
          },
          "& .MuiSelect-select": {
            color: perlColors.textPrimary,
          },
          "& .MuiSvgIcon-root": {
            color: perlColors.textSecondary,
          },
        }}
        {...restProps}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}
        {options.map((option) => {
          const optionValue = typeof option === "object" ? option.value : option;
          const optionLabel = typeof option === "object" ? option.label : option;
          return (
            <MenuItem key={optionValue} value={optionValue}>
              {optionLabel}
            </MenuItem>
          );
        })}
        {props.children}
      </Select>
    </FormControl>
  );
}

